/* When the board and the book disagree under turn order, and what the desk reads.
 *
 * Under the turn-order setting the desk's tap on the assign sheet puts the client
 * with the next technician up, and the booking is then brought into line in
 * Mangomint. The square never waits for that. Whatever does not go through at
 * once becomes an item in the quiet row beside Due now, and this file is every
 * decision about those items that does not need the board: whether to act at all,
 * what an answer from the `optimizer` function comes to, what the row says, and
 * when an item is finished with.
 *
 * Why the wording is careful about what it knows. A save that timed out may well
 * have landed, so it is never "failed" and never "saved"; the desk is told to look.
 * A pair stopped halfway has really moved in part, so it says what moved
 * and names what is left, and a retry that answers `already` then `refused` stays
 * here rather than reading as done. A dry run says it was not saved, because the
 * row otherwise looks exactly like the real thing.
 *
 * Why it never asks anything. No modal, sound or toast, on failure or otherwise.
 * The client is in the chair by now, and the desk comes to this when it has a
 * moment. Everything here is a sentence for that moment.
 *
 * Two kinds of staff id are on an item, and they are not interchangeable. The row's
 * `board_staff_id` and the plan's `toStaffId`s are the board's, because planRoom
 * reasons about the board. `book_staff_id` and everything in the plan's `wire` are
 * Mangomint's, because that is what the book says and what the proxy takes.
 * `names.staff` holds both, and a uuid cannot collide with an integer.
 *
 * What the row keeps on `plan`, beyond planRoom's answer, so that any station can
 * read and act on it without working the book out again:
 *
 *   wire      the moves as the function takes them, in the order to run.
 *   unit      [{ lineId, serviceId, startsAt, label }] the services moving
 *             together. Line ids are reissued on every edit, so the service id is
 *             what finds them again.
 *   bookings  id -> { client, at, fromStaffId } for each OTHER booking being moved.
 *             A move with no entry here is the client's own.
 *   blockers  [{ client, at }] who is in the way, for a stuck plan.
 *   bookName  who the book names, for a technician the board has no row for.
 *   note      the client's note, when it names a technician.
 *
 * Pure. Everything only the store can answer arrives as an argument.
 */
import { notesMention } from './client-notes.js';

var FIX = 'Fix it in Mangomint.';
var NO_WRITE = 'Mangomint can\'t be changed from here, so fix it in Mangomint.';
var UNSURE = 'That save may have gone through; check Mangomint.';

// The function's answers that say this salon or sign-in can't do this at all, so
// trying again would only get the same one.
var NO_RETRY = {
  no_write_key: NO_WRITE,
  no_proxy_url: 'Mangomint isn\'t connected for this salon, so fix it in Mangomint.',
  not_turn_order: 'This salon isn\'t set to turn order any more, so fix it in Mangomint.',
  role_not_allowed: 'This sign-in can\'t change bookings, so fix it in Mangomint.'
};

/* Is this assignment one that brings the book into line? Only the desk's own tap
 * on the sheet, because that tap is what approves a change in Mangomint. A walk-in,
 * a drop on a column, a write-up or a backfill approves nothing, and a square being
 * recorded after the fact is not a client being given to anybody. A desk picking
 * someone other than next up still counts: it is the book that has to agree with
 * the board, whoever the board has. With nobody named there is nothing to disagree
 * with.
 *
 *   followBook, fromSheet, recorded, requested  booleans
 *   bookNamed     the booking names at least one technician
 *   chosenInBook  the technician picked is among them
 */
export function shouldBringBookInLine(a) {
  if (a.followBook || !a.fromSheet || a.recorded || a.requested) return false;
  return !!a.bookNamed && !a.chosenInBook;
}

// A line really saved. `already` counts: a retry of a saved move answers that.
function isSaved(l) {
  return l.outcome === 'moved' || l.outcome === 'already';
}

function movedLines(lastResult) {
  return ((lastResult && lastResult.results) || []).map(function (l, i) {
    return isSaved(l) ? i : -1;
  }).filter(function (i) { return i !== -1; });
}

/* Was a line saved that is not the client's own? Swap moves the others first, so a
 * partial save is usually exactly this, with the unit still on the original
 * technician. Not being able to tell counts as yes: this decides whether the only
 * record of another client's moved booking is kept. */
function savedOnOthers(item) {
  var plan = item.plan || {};
  var moves = plan.moves || [];
  var unit = (plan.unit || []).map(function (u) { return u.lineId; });
  return movedLines(item.last_result).some(function (i) {
    return !moves[i] || unit.indexOf(moves[i].id) === -1;
  });
}

function hasOverlap(lastResult) {
  var lines = lastResult && lastResult.results;
  return !!lines && lines.some(function (l) { return l.overlaps && l.overlaps.length; });
}

/* What an answer from `optimizerApi.applyMoves` comes to: does it leave anything
 * for the desk, and what goes in `last_result`. Everything moved or already there
 * leaves nothing, unless the read-back found an overlap, which is the desk's to
 * know about. Anything else, including an answer nobody can read, leaves an item. */
export function pendingFromResults(answer, nowIso) {
  if (answer && answer.error) {
    return {
      pending: true,
      lastResult: { at: nowIso, error: answer.error, message: answer.message || null }
    };
  }
  var lines = answer && answer.results;
  if (!lines || !lines.length) {
    return { pending: true, lastResult: { at: nowIso, error: 'refused', message: 'Mangomint gave no answer.' } };
  }
  var lastResult = { at: nowIso, dry_run: !!answer.dry_run, finished: true, results: lines };
  var allDone = lines.every(function (l) { return l.outcome === 'moved' || l.outcome === 'already'; });
  return { pending: !allDone || hasOverlap(lastResult), lastResult: lastResult };
}

/* Do these services of one unit overlap each other? `blocks` are { from, to }.
 *
 * A stopgap. The proxy checks each save against the book as it stands, including this
 * visit's own other service, and it has no way to move two at once: the first half of
 * a same-time pair saves, and the second is refused with `staff_unavailable` because
 * the receiver now has the first. That would leave the book split, so such a unit is
 * never sent. It becomes a stuck plan (`same_time_pair`) and the desk moves the two
 * together in Mangomint. Touching ends are not an overlap: a pair booked one after
 * the other saves one at a time without trouble. */
export function unitAtSameTime(blocks) {
  var list = blocks || [];
  return list.some(function (a, i) {
    return list.slice(i + 1).some(function (b) { return a.from < b.to && a.to > b.from; });
  });
}

/* The item for an assign whose plan could not be worked out, so that the board and
 * the book disagreeing still leaves a record. Null when the tap was already known not
 * to bring the book into line (`bring` false), or there is too little to write a row.
 * Not knowing whether it was needed counts as needed: an item the book already agrees
 * with closes on the next read.
 *
 *   a  { boardStaffId, appt, svc, bring }
 */
export function bookMoveAfterFailure(a) {
  var appt = a.appt, svc = a.svc;
  if (a.bring === false || !a.boardStaffId || !appt || appt.id == null || !svc || svc.id == null) return null;
  if (!Number.isFinite(svc.startsAt)) return null;
  var bookId = Number((svc.techIds || [])[0]);
  return {
    appointmentId: appt.id,
    clientName: appt.clientName,
    clientId: appt.clientId,
    tappedServiceId: svc.serviceId,
    boardStaffId: a.boardStaffId,
    bookStaffId: Number.isInteger(bookId) && bookId > 0 ? bookId : null,
    bookName: (svc.techs || [])[0] || '',
    unit: [{ lineId: svc.id, serviceId: svc.serviceId, startsAt: svc.startsAt, label: svc.name }],
    plan: { kind: 'stuck', moves: [], why: '', reason: 'plan_failed', blocking: [] },
    wire: [], bookings: {}, blockers: []
  };
}

/* A clean plan's row goes up before its call, so that a tab closed in between still
 * leaves a record. For as long as the call can be running, and every station can see
 * the row by then, it is not yet anything for the desk: a move that goes through says
 * nothing. The row stays hidden until an answer has finished or that time has passed. */
export function onFirstTry(item, nowMs, waitMs) {
  if (!item.plan || item.plan.kind !== 'clean') return false;
  var last = item.last_result;
  if (last && last.finished !== false) return false;
  return nowMs - new Date(item.created_at || 0).getTime() <= waitMs;
}

/* A call that never got a usable answer, as the same shape an answer has.
 *
 * Not hearing back is not knowing: the request may have reached the function, which
 * goes on without us, so it reads as "may have gone through". The same goes for a
 * 5xx, because the lines run one at a time and it could have died after some of
 * them. Only a station that knows it is offline can say nothing was sent. */
export function answerFromFailure(err, online) {
  if (err && err.timedOut) return { error: 'timeout' };
  var status = err && err.status;
  if (status == null) return { error: online ? 'timeout' : 'offline' };
  if (NO_RETRY[err.said]) return { error: err.said };
  if (status >= 500) return { error: 'timeout' };
  return { error: 'refused', message: err.said || err.message || null };
}

/* Which of these technicians the note names. All of them and not just the one
 * receiving: "Requesting to ONLY be seen by Ann" says nothing about Tam by name,
 * and it is exactly the note the desk needs to see before giving her to Tam. */
export function noteNamingStaff(notes, staffNames) {
  if (!notes) return [];
  return (staffNames || []).filter(function (n) { return notesMention(notes, n); });
}

// "Ana Rivera" as "Ana R.", the way the design's items read.
export function shortClient(name) {
  var parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] || '';
  return parts[0] + ' ' + parts[parts.length - 1].charAt(0).toUpperCase() + '.';
}

function pad(n) { return (n < 10 ? '0' : '') + n; }

/* The book's start as the wall-clock string the proxy compares against. The board
 * holds it as a local instant (see `apptInstant` in js/plugins/mangomint.js), so
 * reading the local parts back gives what Mangomint wrote. */
export function localStart(ms) {
  var d = new Date(ms);
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + 'T' +
    pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds());
}

/* A row for tt_pending_moves. `starts_at` follows the mirror: wall-clock, labelled
 * UTC, which is what the schema's comment on it says. */
export function pendingRow(a) {
  var first = a.unit[0];
  var note = noteNamingStaff(a.notes, a.staffNames).length ? a.notes : '';
  return {
    id: a.id,
    date: a.date,
    appointment_id: a.appointmentId,
    service_line_id: first.lineId,
    client_label: shortClient(a.clientName),
    service_label: a.unit.map(function (u) { return u.label; }).join(' + '),
    starts_at: localStart(first.startsAt) + 'Z',
    board_staff_id: a.boardStaffId,
    book_staff_id: a.bookStaffId == null ? null : a.bookStaffId,
    plan: Object.assign({}, a.plan, {
      wire: a.wire || [],
      unit: a.unit,
      bookings: a.bookings || {},
      blockers: a.blockers || [],
      bookName: a.bookName || '',
      note: note
    }),
    last_result: a.lastResult || null,
    closed_at: null
  };
}

// "12:30" from the stored string, read straight off its clock: the mirror's
// convention means converting it to anything would make it wrong.
export function clockOf(startsAt) {
  var m = /T(\d{2}):(\d{2})/.exec(String(startsAt || ''));
  if (!m) return '';
  return (Number(m[1]) % 12 || 12) + ':' + m[2];
}

function nameOf(names, id) {
  var staff = (names && names.staff) || {};
  return id != null && staff[id] ? staff[id] : '';
}

function bookingOf(names, id) {
  return ((names && names.bookings) || {})[id] || null;
}

function who(b) {
  return b.client + (b.at ? ' at ' + b.at : '');
}

// A client's name often ends in an initial's full stop, and that one will do.
function endSentence(text) {
  return /\.$/.test(text) ? text : text + '.';
}

// Who is in the way and who could take them, in the design's words:
// "Yulisa has Mai T. at 12:30. Thu could take Mai T. once Lan takes Jo P."
function swapSentence(item, names) {
  var board = item.board_staff_id;
  var boardName = nameOf(names, board) || 'They';
  var others = ((item.plan && item.plan.moves) || [])
    .map(function (m) { return { move: m, booking: bookingOf(names, m.id) }; })
    .filter(function (o) { return o.booking; });
  if (!others.length) return '';
  var direct = others.filter(function (o) { return o.booking.fromStaffId === board; });
  var rest = others.filter(function (o) { return o.booking.fromStaffId !== board; });
  var has = endSentence(boardName + ' has ' + direct.map(function (o) { return who(o.booking); }).join(' and '));
  var offers = direct.map(function (o) {
    var chained = rest.filter(function (r) { return r.booking.fromStaffId === o.move.toStaffId; });
    rest = rest.filter(function (r) { return chained.indexOf(r) === -1; });
    return endSentence((nameOf(names, o.move.toStaffId) || 'Somebody') + ' could take ' + o.booking.client +
      (chained.length ? ' once ' + chained.map(function (c) {
        return (nameOf(names, c.move.toStaffId) || 'somebody') + ' takes ' + c.booking.client;
      }).join(' and ') : ''));
  });
  return direct.length ? has + ' ' + offers.join(' ') : offers.join(' ');
}

function stuckSentence(item, names) {
  var plan = item.plan;
  var first = plan.blockers && plan.blockers[0];
  switch (plan.reason) {
    case 'requested':
      return (first ? who(first) + ' is in the way and was' : 'A booking in the way was') +
        ' asked for by name, so it stays. ' + FIX;
    case 'unmovable':
      return (first ? who(first) + ' is' : 'A booking is') +
        ' in the way and can\'t be moved from here. ' + FIX;
    case 'no_receiver':
      return endSentence('Nobody else is free and able to take ' + (first ? who(first) : 'the booking in the way')) + ' ' + FIX;
    case 'too_many':
      return endSentence('Making room would take moving more than two other bookings' +
        (first ? ', starting with ' + who(first) : '')) + ' ' + FIX;
    case 'receiver':
      return (nameOf(names, item.board_staff_id) || 'That technician') +
        ' can\'t take this in the book: off, on a break, with a client, or not set up for it. ' + FIX;
    case 'no_mangomint_id':
      return 'A technician here isn\'t linked to Mangomint, so this can\'t be moved from here. ' + FIX;
    case 'pair_requested':
      return 'The other half of this visit was asked for by name, so it stays. ' + FIX;
    case 'same_time_pair':
      return 'These services are at the same time, so they have to be moved together in Mangomint.';
    case 'plan_failed':
      return 'This couldn\'t be worked out here. ' + FIX;
    default:
      return plan.why ? plan.why + ' ' + FIX : FIX;
  }
}

function labelOfLine(item, names, move) {
  var other = bookingOf(names, move.id);
  if (other) return other.client;
  var u = ((item.plan && item.plan.unit) || []).find(function (x) { return x.lineId === move.id; });
  return u && u.label ? u.label : item.service_label;
}

function movedLabels(item, names) {
  var moves = (item.plan && item.plan.moves) || [];
  return movedLines(item.last_result)
    .map(function (i) { return moves[i]; })
    .filter(Boolean)
    .map(function (m) { return labelOfLine(item, names, m); });
}

/* The square no longer being where the item says outranks whatever the plan says:
 * running it now would move other clients for a technician who hasn't got this one. */
function squareSentence(item, names) {
  var square = names && names.square;
  var moved = movedLabels(item, names);
  if ((item.plan && item.plan.retargeted) || square === 'elsewhere') {
    return (moved.length ? endSentence('Already moved: ' + moved.join(', ')) + ' ' : '') +
      'The square moved, so fix it in Mangomint.';
  }
  if (square === 'gone') {
    return moved.length
      ? endSentence('The square is off the board, but this already moved in Mangomint: ' + moved.join(', ')) + ' ' + FIX
      : 'The square is off the board, so nothing here will be moved.';
  }
  return '';
}

/* What the last try came to, on top of what the plan says. `replaces` means the
 * plan's sentence no longer describes things: part of it has happened, or it all
 * has. */
function readResult(item, names) {
  var last = item.last_result;
  // A clean plan is sent as soon as it is made, so a row with no answer on it is a
  // call whose answer was lost, most likely with the tab that made it.
  if (!last) return item.plan && item.plan.kind === 'clean' ? { text: UNSURE, canSwap: true, replaces: true } : null;
  if (last.error) {
    if (NO_RETRY[last.error]) return { text: NO_RETRY[last.error], canSwap: false };
    if (last.error === 'timeout') return { text: UNSURE, canSwap: true, replaces: true };
    if (last.error === 'offline') return { text: 'Not sent: this station is offline.', canSwap: true };
    return { text: 'Not saved' + (last.message ? ': ' + last.message : '.'), canSwap: true };
  }
  var lines = last.results || [];
  // The function writes after every line, so an unfinished record is a run that
  // was killed or is still going. It says nothing about the lines not reached.
  if (last.finished === false) return { text: UNSURE, canSwap: true, replaces: true };
  var isDone = function (l) { return l.outcome === 'moved' || l.outcome === 'already'; };
  if (lines.length && lines.every(isDone)) {
    if (hasOverlap(last)) {
      return {
        text: 'It moved, but Mangomint shows an overlap on ' + (nameOf(names, item.board_staff_id) || 'that technician') + '. ' + FIX,
        canSwap: false, replaces: true
      };
    }
    return { text: 'Saved. Waiting for the book to catch up.', canSwap: false, replaces: true };
  }
  var stop = lines.find(function (l) { return l.outcome === 'refused' || l.outcome === 'waiting'; });
  if (lines.some(isDone)) {
    var moves = (item.plan && item.plan.moves) || [];
    var left = lines.map(function (l, i) { return isDone(l) ? null : moves[i]; })
      .filter(Boolean)
      .map(function (m) { return labelOfLine(item, names, m); });
    return {
      text: 'Part of this moved.' + (left.length ? ' Still to move: ' + endSentence(left.join(', ')) : '') +
        (stop && stop.why ? ' ' + stop.why : (last.why ? ' ' + last.why : '')),
      canSwap: true, replaces: true
    };
  }
  if (stop && stop.outcome === 'waiting') {
    return { text: 'Waiting' + (stop.why ? ': ' + stop.why : ' on Mangomint.'), canSwap: true };
  }
  if (stop) return { text: 'Not saved' + (stop.why ? ': ' + stop.why : '.'), canSwap: true };
  if (lines.some(function (l) { return l.outcome === 'dry_run'; })) {
    return { text: 'Dry run, so this was not saved.', canSwap: true };
  }
  return { text: 'Not saved.', canSwap: true };
}

/* What the row says about one item.
 *
 *   names  { staff: id -> name, bookings: id -> { client, at, fromStaffId },
 *            writeKeySet: false when this salon is known to have no write key,
 *            square: 'here' | 'elsewhere' | 'gone' from `squareOf`, or 'unknown'
 *            until this station has read today's squares from the server }
 *
 * Returns { headline, detail, canSwap, action, note }. `action` is what the button
 * says when there is one: a clean plan has nothing to swap, it only has to try again.
 */
export function pendingLine(item, names) {
  var plan = item.plan || {};
  var bookName = nameOf(names, item.book_staff_id) || plan.bookName || 'someone else';
  var headline = [item.client_label, clockOf(item.starts_at), item.service_label].filter(Boolean).join(' ') +
    ': with ' + (nameOf(names, item.board_staff_id) || 'another technician') +
    ', still ' + bookName + ' in the book.';
  var action = plan.kind === 'swap' ? 'Swap' : 'Try again';
  var note = plan.note || '';

  var off = squareSentence(item, names);
  if (off) return { headline: headline, detail: off, canSwap: false, action: action, note: note };

  if (plan.kind === 'stuck') {
    return { headline: headline, detail: stuckSentence(item, names), canSwap: false, action: action, note: note };
  }

  var result = readResult(item, names);
  var base = result && result.replaces ? '' : swapSentence(item, names);
  // A board whose squares haven't been read can't vouch for this one.
  var canSwap = (result ? result.canSwap : true) && !(names && names.square === 'unknown');
  var parts = [base, result ? result.text : ''];
  if (canSwap && names && names.writeKeySet === false) {
    canSwap = false;
    parts.push(NO_WRITE);
  }
  return {
    headline: headline,
    detail: parts.filter(Boolean).join(' '),
    canSwap: canSwap,
    action: action,
    note: note
  };
}

/* Does the book name this technician on this line? By id alone when both sides have
 * one: the label only carries a first name, and two people can share one. The label
 * is the fallback, for a technician nobody has linked or a book with no ids. */
export function namedOnLine(staff, line, matchesLabel) {
  var ids = (line && line.techIds) || [];
  if (staff.externalStaffId && ids.length) return ids.indexOf(staff.externalStaffId) !== -1;
  return ((line && line.techs) || []).some(function (t) { return matchesLabel(staff, t); });
}

/* The latest book's word on an item, for `stillOpen`.
 *
 * Only an explicit cancellation or a checkout ends an item from here. A visit or a
 * line that is absent says nothing: the ingest deletes a visit's lines and re-inserts
 * them with no transaction, so a read can land in the gap, most likely during the
 * partial save of a Swap, and a closed item never reopens. So absent is null, nothing
 * to judge by. A booking rescheduled off today stays open until the day ends, which
 * is the cheaper mistake.
 *
 *   ctx  { staff, matchesLabel, checkedOut }
 */
export function bookRowFromVisit(item, appt, ctx) {
  if (appt && appt.cancelled) return { cancelled: true };
  if (ctx.checkedOut) return { checkedOut: true };
  if (!appt) return null;
  var services = appt.services || [];
  var unit = (item.plan && item.plan.unit) || [];
  var wanted = unit.length ? unit : [{ lineId: item.service_line_id }];
  var lines = wanted.map(function (u) {
    return services.find(function (x) { return x.id === u.lineId; }) ||
      services.find(function (x) { return u.serviceId != null && x.serviceId === u.serviceId; });
  });
  if (lines.some(function (x) { return !x; })) return null;
  return {
    lines: lines,
    staffIds: ctx.staff.filter(function (s) {
      return lines.every(function (x) { return namedOnLine(s, x, ctx.matchesLabel); });
    }).map(function (s) { return s.id; }),
    namesOriginal: item.book_staff_id != null && lines.every(function (x) {
      return (x.techIds || []).indexOf(String(item.book_staff_id)) !== -1;
    })
  };
}

function turnIsFor(t, item, unit) {
  if (String(t.appointmentId) !== String(item.appointment_id)) return false;
  if (!unit.length) return true;
  return unit.some(function (u) {
    return (t.appointmentServiceId != null && t.appointmentServiceId === u.lineId) ||
      (t.sourceServiceId != null && u.serviceId != null && String(t.sourceServiceId) === String(u.serviceId));
  });
}

/* Where the square an item describes is now: { where: 'here' | 'gone' } or
 * { where: 'elsewhere', staffId }. By service and not only appointment, since a visit
 * can rightly have another service's square on somebody else, and looking inside
 * `extras`, since a service can be folded into another square. */
export function squareOf(item, turns) {
  var unit = (item.plan && item.plan.unit) || [];
  var holders = [];
  (turns || []).forEach(function (t) {
    if (turnIsFor(t, item, unit) || (t.extras || []).some(function (x) { return turnIsFor(x, item, unit); })) {
      holders.push(t.staffId);
    }
  });
  if (holders.indexOf(item.board_staff_id) !== -1) return { where: 'here' };
  return holders.length ? { where: 'elsewhere', staffId: holders[0] } : { where: 'gone' };
}

/* What a reconcile does about the square: 'retarget', 'gone' or 'none'.
 *
 * A square on somebody else is a live disagreement with a new name on it, so the item
 * follows it, unless this item was only just made or retargeted: a station holding a
 * stale square would otherwise take a new item's Swap for good, or send a retargeted
 * one straight back. A missing square only
 * counts after the grace, because a station can hear about an item before the square
 * that went up first. Neither delay applies to Swap, which `pendingLine` takes away
 * as soon as `names.square` says so. */
export function squareAction(item, square, nowMs, graceMs) {
  if (square.where === 'elsewhere') {
    var at = (item.plan && item.plan.retargetedAt) || item.created_at || 0;
    return nowMs - new Date(at).getTime() <= graceMs ? 'none' : 'retarget';
  }
  if (square.where === 'gone') {
    return nowMs - new Date(item.created_at || 0).getTime() > graceMs ? 'gone' : 'none';
  }
  return 'none';
}

/* What to do with a new assign, given what is open.
 *
 * An item already covering the tapped line on this technician is already the record
 * of this disagreement. That is the second half of a pair A+B: A is seated, so the new
 * unit is only B, and acting on it would close the pair's item and move half a pair
 * alone. So nothing happens, no new item and no call. An item is replaced only when
 * the technician differs or the new unit covers all of it.
 *
 *   p  { appointmentId, boardStaffId, unit, tappedServiceId } */
export function assignAgainstOpen(openItems, p) {
  var services = p.unit.map(function (u) { return u.serviceId; });
  var related = (openItems || []).filter(function (item) {
    return String(item.appointment_id) === String(p.appointmentId) &&
      ((item.plan && item.plan.unit) || []).some(function (u) { return services.indexOf(u.serviceId) !== -1; });
  });
  var covered = related.some(function (item) {
    return item.board_staff_id === p.boardStaffId &&
      item.plan.unit.some(function (u) { return u.serviceId === p.tappedServiceId; });
  });
  if (covered) return { skip: true, replace: [] };
  return {
    skip: false,
    replace: related.filter(function (item) {
      return item.board_staff_id !== p.boardStaffId ||
        item.plan.unit.every(function (u) { return services.indexOf(u.serviceId) !== -1; });
    })
  };
}

/* A later try must not wipe the record of what an earlier one saved: what moved stays
 * moved, whatever the later try says of that line, and `stillOpen` and the row both
 * read it. A line overridden may have been the one that stopped the run, so its
 * reason is kept as `why`. */
export function mergeLastResult(prev, next) {
  var before = (prev && prev.results) || [];
  if (!before.length) return next;
  if (!next.results) return Object.assign({}, next, { results: before });
  var why = null;
  var changed = false;
  var lines = next.results.map(function (l) {
    var was = before.find(function (b) { return b.id === l.id; });
    if (!was || !isSaved(was) || isSaved(l)) return l;
    changed = true;
    why = why || l.why || null;
    return was;
  });
  return changed ? Object.assign({}, next, { results: lines, why: why }) : next;
}

/* Is this item still the desk's to sort out, on the latest read of the book?
 *
 *   bookRow  { staffIds, cancelled, checkedOut, squareGone, namesOriginal, overlaps },
 *            with `staffIds` the board's ids for who the book names, or null when
 *            there is nothing to judge by, which leaves the item where it is.
 *
 * `squareGone` is a square deleted outright, past the grace. With nothing saved that
 * ends it. With a move of the client's own already saved the book has really changed
 * for someone who now has no square, so it stays until the book names the original
 * technician again. With another client's booking saved it stays regardless: their
 * move is not undone by the book naming the original on this one. A save that landed on an overlap stays while the overlap does, or the only
 * record of it would close the moment it was written.
 */
export function stillOpen(item, bookRow) {
  if (!bookRow) return true;
  if (bookRow.cancelled || bookRow.checkedOut) return false;
  if (bookRow.squareGone) {
    if (savedOnOthers(item)) return true;
    return movedLines(item.last_result).length > 0 && !bookRow.namesOriginal;
  }
  if ((bookRow.staffIds || []).indexOf(item.board_staff_id) === -1) return true;
  return hasOverlap(item.last_result) && !!bookRow.overlaps;
}
